// SHADOW MARKET™ — Chart Patterns
// Data layer for the 16 chart-pattern lessons displayed inside the Academy.

export const PATRONES = [
  {
    id: "bandera-alcista",
    nombre: "Bullish Flag",
    tipo: "continuacion",
    direccion: "alcista",
    nivel: "basico",
    descripcion:
      "Bullish continuation pattern formed by a strong upward impulse (the pole) followed by a correction in a parallel descending channel (the flag). Indicates a pause before the trend continues upward.",
    estructura: [
      "Pole: strong bullish impulse",
      "Parallel descending corrective channel (the flag)",
      "Upward breakout of the channel with volume",
    ],
    objetivo: "The height of the pole projected upward from the breakout point.",
    consejo:
      "Look for decreasing volume during the flag and an increase on the breakout. More reliable on 4H or daily timeframes. Very frequent in BTC after positive news.",
  },
  {
    id: "bandera-bajista",
    nombre: "Bearish Flag",
    tipo: "continuacion",
    direccion: "bajista",
    nivel: "basico",
    descripcion:
      "Bearish continuation pattern. A strong downward impulse (pole) followed by an upward correction in a parallel channel (flag). Signal of bearish trend continuation.",
    estructura: [
      "Pole: strong bearish impulse",
      "Parallel ascending corrective channel",
      "Downward breakout of the channel",
    ],
    objetivo: "The height of the pole projected downward from the breakout point.",
    consejo:
      "The corrective channel should remain below 50% of the pole. If it exceeds that level, the pattern loses reliability.",
  },
  {
    id: "banderin-alcista",
    nombre: "Bullish Pennant",
    tipo: "continuacion",
    direccion: "alcista",
    nivel: "basico",
    descripcion:
      "Similar to the bullish flag but the correction forms a converging triangle instead of a parallel channel. Consolidation is more compact and the breakout tends to be more explosive.",
    estructura: [
      "Pole: bullish impulse",
      "Converging consolidation triangle",
      "Upward breakout of the triangle",
    ],
    objetivo: "The height of the pole projected from the triangle apex upward.",
    consejo:
      "The triangle should form in the upper half of the pole. The closer to the apex the breakout occurs, the more powerful the movement tends to be.",
  },
  {
    id: "banderin-bajista",
    nombre: "Bearish Pennant",
    tipo: "continuacion",
    direccion: "bajista",
    nivel: "basico",
    descripcion:
      "Bearish version of the pennant. The converging triangle forms after a strong bearish impulse. Continuation signal: the downward breakout confirms the movement.",
    estructura: [
      "Pole: bearish impulse",
      "Converging consolidation triangle",
      "Downward breakout of the triangle",
    ],
    objetivo: "The height of the pole projected downward from the apex.",
    consejo:
      "Confirm with increased volume on the breakout. Pennants on short timeframes (15min-1H) are very frequent in crypto.",
  },
  {
    id: "taza-asa",
    nombre: "Cup and Handle",
    tipo: "continuacion",
    direccion: "alcista",
    nivel: "intermedio",
    descripcion:
      "Smooth U-shaped correction (the cup) followed by a small bearish consolidation (the handle). One of the most studied patterns in bull markets.",
    estructura: [
      "Prior bullish impulse",
      "U-shaped correction (the cup)",
      "Small bearish correction (the handle)",
      "Breakout above resistance",
    ],
    objetivo: "The depth of the cup projected upward from the resistance level.",
    consejo:
      "The cup should be rounded (U), not V-shaped. The handle should not correct more than 50% of the cup. Very common in BTC on weekly timeframes.",
  },
  {
    id: "taza-asa-invertida",
    nombre: "Inverted Cup and Handle",
    tipo: "continuacion",
    direccion: "bajista",
    nivel: "intermedio",
    descripcion:
      "Bearish version. The correction forms an inverted dome followed by a small bullish correction. Bearish continuation signal.",
    estructura: [
      "Prior bearish impulse",
      "Inverted U-shaped correction (dome)",
      "Small bullish correction (handle)",
      "Bearish breakout of support",
    ],
    objetivo: "The depth of the dome projected downward from support.",
    consejo:
      "Frequent in prolonged bear markets. Confirm with increasing volume on the downward breakout.",
  },
  {
    id: "doble-techo",
    nombre: "Double Top",
    tipo: "reversion",
    direccion: "bajista",
    nivel: "basico",
    descripcion:
      "Two consecutive highs at the same level with an intermediate low. Signal that buyers cannot overcome that resistance. The break below the neckline confirms the reversal.",
    estructura: [
      "First high",
      "Correction to the neckline",
      "Second high at the same level",
      "Bearish breakout of the neckline",
    ],
    objetivo: "Distance between the high and the neckline, projected downward.",
    consejo:
      "The second high with lower volume increases reliability. Confirm with close below the neckline.",
  },
  {
    id: "doble-suelo",
    nombre: "Double Bottom",
    tipo: "reversion",
    direccion: "alcista",
    nivel: "basico",
    descripcion:
      "Two consecutive lows at the same level with an intermediate high. Sellers cannot push the price below that support. Bullish breakout of the neckline confirms the reversal.",
    estructura: [
      "First low",
      "Bounce to the neckline",
      "Second low at the same level",
      "Bullish breakout of the neckline",
    ],
    objetivo: "Distance between the low and the neckline, projected upward.",
    consejo:
      "Second low with lower volume is a positive signal. Confirm with close above the neckline.",
  },
  {
    id: "triple-techo",
    nombre: "Triple Top",
    tipo: "reversion",
    direccion: "bajista",
    nivel: "intermedio",
    descripcion:
      "Three highs at the same level. More reliable than the double top. Each failed attempt to overcome resistance increases selling pressure.",
    estructura: [
      "Three consecutive highs at the same level",
      "Two intermediate corrections",
      "Break below the neckline",
    ],
    objetivo: "Distance between the high and the neckline, projected downward.",
    consejo: "The longer it takes to form, the more powerful the subsequent decline tends to be.",
  },
  {
    id: "triple-suelo",
    nombre: "Triple Bottom",
    tipo: "reversion",
    direccion: "alcista",
    nivel: "intermedio",
    descripcion:
      "Three lows at the same level. One of the most powerful bullish reversal signals. Support is reinforced with each bounce.",
    estructura: [
      "Three consecutive lows at the same level",
      "Two intermediate bounces",
      "Breakout above the neckline",
    ],
    objetivo: "Distance between the low and the neckline, projected upward.",
    consejo:
      "In crypto, triple bottoms on long-term support frequently mark important market bottoms.",
  },
  {
    id: "cabeza-hombros",
    nombre: "Head and Shoulders",
    tipo: "reversion",
    direccion: "bajista",
    nivel: "basico",
    descripcion:
      "The most well-known reversal pattern. Three highs: the central one (head) exceeds the two outer ones (shoulders) which are at the same level. Break below the neckline confirms the bearish reversal.",
    estructura: [
      "Left shoulder",
      "Head (higher high)",
      "Right shoulder (≈same level as left)",
      "Neckline + bearish breakout",
    ],
    objetivo: "Distance between the head and the neckline, projected from the breakout.",
    consejo:
      "Upward-sloping neckline is more bearish. High volume on the breakout confirms the pattern.",
  },
  {
    id: "cabeza-hombros-inversos",
    nombre: "Inverse H&S",
    tipo: "reversion",
    direccion: "alcista",
    nivel: "basico",
    descripcion:
      "Bullish version of head and shoulders. Three lows where the central one (head) is the lowest. Breakout above the neckline signals a powerful bullish reversal.",
    estructura: [
      "Left shoulder",
      "Head (lower low)",
      "Right shoulder (≈same level as left)",
      "Neckline + bullish breakout",
    ],
    objetivo: "Distance between the head and the neckline, projected upward.",
    consejo:
      "One of the most reliable patterns for identifying bottoms. Look for increasing volume on the right shoulder and on the breakout.",
  },
  {
    id: "cuna-ascendente",
    nombre: "Rising Wedge",
    tipo: "reversion",
    direccion: "bajista",
    nivel: "intermedio",
    descripcion:
      "Converging channel sloping upward. Both lines rise but converge: the price has less and less room to rise. Bearish reversal signal.",
    estructura: [
      "Bullish resistance line",
      "Bullish support line (with steeper slope)",
      "Convergence of both lines",
      "Downward breakout",
    ],
    objetivo: "Height of the wedge base projected downward from the breakout point.",
    consejo:
      "Rising wedges frequently appear at the end of bullish impulses. They are signals of institutional distribution.",
  },
  {
    id: "cuna-descendente",
    nombre: "Falling Wedge",
    tipo: "reversion",
    direccion: "alcista",
    nivel: "intermedio",
    descripcion:
      "Converging channel sloping downward. Selling pressure reduces with each low. Bullish reversal signal: the upward breakout tends to be powerful.",
    estructura: [
      "Bearish resistance line",
      "Bearish support line (with gentler slope)",
      "Convergence of both lines",
      "Upward breakout",
    ],
    objetivo: "Height of the wedge base projected upward from the breakout point.",
    consejo:
      "Falling wedges in corrections of bullish trend are very reliable continuation signals in BTC/ETH.",
  },
  {
    id: "triangulo",
    nombre: "Symmetrical Triangle",
    tipo: "neutro",
    direccion: "neutro",
    nivel: "basico",
    descripcion:
      "Consolidation zone with descending highs and ascending lows. Price compresses until the energy produces a breakout. The direction of the breakout determines the target.",
    estructura: [
      "Descending resistance line",
      "Ascending support line",
      "Convergence toward an apex",
      "Breakout (indeterminate direction)",
    ],
    objetivo: "Height of the triangle base projected in the direction of the breakout.",
    consejo:
      "Context determines probability: a triangle in bullish trend usually breaks upward. Always confirm with candle close outside.",
  },
  {
    id: "rectangulo",
    nombre: "Rectangle",
    tipo: "neutro",
    direccion: "neutro",
    nivel: "basico",
    descripcion:
      "Price oscillates between horizontal support and resistance. Accumulation or distribution zone. The direction of the breakout determines the bias and target.",
    estructura: [
      "Horizontal resistance",
      "Horizontal support",
      "Oscillations between both",
      "Breakout (indeterminate direction)",
    ],
    objetivo: "Height of the rectangle projected in the direction of the breakout.",
    consejo:
      "In crypto, long rectangles with increasing volume at lows usually precede powerful bullish breakouts (institutional accumulation).",
  },
];

export const TIPOS = ["continuacion", "reversion", "neutro"];
export const NIVELES = ["basico", "intermedio"];

export const TIPO_LABEL = {
  continuacion: "Continuation",
  reversion: "Reversal",
  neutro: "Neutral",
};

export const DIRECCION_LABEL = {
  alcista: "Bullish",
  bajista: "Bearish",
  neutro: "Neutral",
};

export const NIVEL_LABEL = {
  basico: "Basic",
  intermedio: "Intermediate",
};

export function getPatron(id) {
  return PATRONES.find((p) => p.id === id) || null;
}

export function filtrarPatrones(tipo, nivel) {
  return PATRONES.filter((p) => {
    if (tipo && tipo !== "todos" && p.tipo !== tipo) return false;
    if (nivel && nivel !== "todos" && p.nivel !== nivel) return false;
    return true;
  });
}